<template>
  <CalendarsStyled>
    <PanelTableMolecule
      :isLoading="loading"
      :count="totalCount"
      :title="$tc('calendar', 2) | capitalize"
      :columns="columns"
      :rows="tableData"
      :pageIndex="page + 1"
      :pageSize="perPage"
      :createRouteName="'createCalendar'"
      @pageNumberChange="pageNumberChange($event)"
      @reload="reload"
    />
    <router-view />
  </CalendarsStyled>
</template>

<script>
import { styled } from '@egoist/vue-emotion'
import get from 'lodash/get'
import { getRolesFromToken } from '@common/utils'
import { Edit2Icon, SearchIcon, TrashIcon } from 'vue-feather-icons'

import CALENDARS_QUERY from '#/graphql/calendar/list.gql'
import DELETE_CALENDAR_MUTATION from '#/graphql/calendar/delete.gql'

import EntityLinkAtom from '@/components/Atomic/Atoms/EntityLinkAtom'
import PanelTableMolecule from '@/components/Atomic/Molecules/PanelTableMolecule'
import TableColumnSearch from '@/components/Atomic/Molecules/TableColumnSearch'

const CalendarsStyled = styled('div')`
  margin: 1rem;
  .actions {
    display: flex;
    flex-direction: row;
    padding: 5px;
    justify-content: space-around;

    color: ${p => p.theme.colors.primary};

    > div {
      cursor: pointer;
      &:hover {
        color: ${props => props.theme.colors.white};
      }
    }
  }
`

export default {
  components: {
    CalendarsStyled,
    PanelTableMolecule,
  },
  data() {
    return {
      loading: true,
      count: 0,
      totalCount: 0,
      models: [],
      errors: [],
      searchName: '',
      where: undefined,
      perPage: 10,
      page: 0,
      selectedOwnerType: null,
    }
  },
  computed: {
    userRoles() {
      return getRolesFromToken(this.$keycloak.token)
    },
    hasWritePermission() {
      return this.userRoles.includes('archon') || this.userRoles.includes('talpa-product')
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.perPage)
    },
    skip() {
      return this.page * this.perPage
    },
    columns() {
      const header = [
        {
          field: 'name',
          key: 'a',
          title: this.$tc('name', 2),
          align: 'left',
          filterCustom: {
            defaultVisible: false,
            render: ({ closeFn }) => {
              return (
                <TableColumnSearch
                  searchValue={this.searchName}
                  placeholder="Search name"
                  on-valueChange={e => (this.searchName = e)}
                  on-cancel={() => this.searchCancel(closeFn)}
                  on-confirm={() => this.searchConfirm(closeFn)}
                />
              )
            },
            // custom filter icon
            filterIcon: () => {
              return <SearchIcon size="1.3x" />
            },
          },
          renderBodyCell: ({ row }) => {
            const { id, type, name } = row
            if (!id || !type || !name) {
              return `something missing`
            }
            return <EntityLinkAtom type={type} id={id} label={name} />
          },
        },
        {
          field: 'ownerType',
          key: 'ownerType',
          title: `${this.$tc('owner', 1)} ${this.$tc('type', 1)}`,
          align: 'center',
          filter: {
            filterList: this.availableOwnerTypes,
            isMultiple: true,
            // filter confirm hook
            filterConfirm: filterList => {
              const selectedIds = filterList.filter(x => x.selected).map(x => x.value)
              this.selectedOwnerType = selectedIds.length > 0 ? selectedIds : null
            },
            // filter reset hook
            filterReset: () => {
              this.selectedOwnerType = null
            },
            filterIcon: () => {
              return <SearchIcon size="1.3x" />
            },
          },
        },
        {
          field: 'owner',
          key: 'owner',
          title: this.$tc('owner', 1),
          align: 'center',
          renderBodyCell: ({ row }) => {
            const { id, type, label } = row?.owner
            if (!id || !type || !label) {
              return `something missing`
            }
            return <EntityLinkAtom type={type} id={id} label={label} />
          },
        },
        { field: 'schedules', key: 'schedules', title: this.$tc('schedule', 2), align: 'center' },
        {
          field: 'actions',
          key: 'i',
          title: 'Actions',
          width: '',
          center: 'left',
          renderBodyCell: ({ row }) => {
            return (
              <div class="actions">
                <div on-click={() => this.confirmDelete(row)}>
                  <TrashIcon size="1.3x" />
                </div>
                <div on-click={() => this.$router.push({ name: 'calendar', params: { id: row?.rowKey } })}>
                  <Edit2Icon size="1.3x" />
                </div>
              </div>
            )
          },
        },
      ]
      return header
    },
    tableData() {
      const tableData = this.models.map(calendar => {
        return {
          name: calendar.name,
          ownerType: calendar.owner.__typename,
          owner: {
            type: calendar?.owner?.__typename,
            id: calendar?.owner?.id,
            label: calendar?.owner?.name,
          },
          schedules: calendar.schedules.length,
          rowKey: calendar.id,
          id: calendar.id,
          type: calendar.__typename,
        }
      })
      return tableData
    },
    availableOwnerTypes() {
      return [
        {
          value: 'Organization',
          label: 'Organization',
          selected: false,
        },
        {
          value: 'Subsidiary',
          label: 'Subsidiary',
          selected: false,
        },
        {
          value: 'User',
          label: 'User',
          selected: false,
        },
      ]
    },
  },
  methods: {
    reload() {
      this.where = ''
      this.selectedOwnerType = null
      this.$apollo.queries.models.refetch()
    },
    pageNumberChange(pageIndex) {
      this.page = pageIndex - 1
    },
    searchCancel(closeFn) {
      this.searchName = ''
      closeFn()
    },
    searchConfirm(closeFn) {
      closeFn()
    },
    search() {
      let AND = []
      if (this.searchName) {
        AND.push({
          name: {
            contains: this.searchName,
            mode: 'insensitive',
          },
        })
      }
      if (this.selectedOwnerType) {
        AND.push({
          ownerType: {
            in: this.selectedOwnerType,
          },
        })
      }
      if (AND.length > 0) {
        this.where = { AND }
      } else {
        this.where = undefined
      }
    },
    confirmDelete(model) {
      this.$root.$emit(
        'activateOverlay',
        'ConfirmDeleteOverlay',
        {
          type: 'Calendar',
          instance: model,
          labelKey: 'name',
          onConfirm: this.delete,
          onConfirmArgs: model,
        },
        this.onCloseSettings,
      )
    },
    async delete({ id }) {
      const res = await this.$apollo.mutate({
        mutation: DELETE_CALENDAR_MUTATION,
        variables: {
          where: {
            id,
          },
        },
      })
      if (res) {
        this.$root.$emit('closeOverlay')
        // this.$emit('reload')
        this.reload()
      }
      this.$root.$emit('closeOverlay')
    },
  },
  watch: {
    searchName: {
      handler(s) {
        this.page = 0
        if (s) {
          this.searchName = s
        } else {
          this.searchName = undefined
        }
        this.search()
      },
    },
    selectedOwnerType: {
      handler() {
        this.page = 0
        this.search()
      },
    },
  },
  apollo: {
    models: {
      query: CALENDARS_QUERY,
      variables() {
        return {
          take: this.perPage,
          skip: this.skip,
          where: this.where ? this.where : undefined,
        }
      },
      manual: true,
      result({ data, loading }) {
        this.loading = loading
        this.models = data?.calendars
        this.totalCount = data?.aggregateCalendar?._count?.id
        this.count = get(data, 'aggregateCalendar._count._all', 0)
        this.totalCount = get(data, 'aggregateCalendar._count._all', 0)
      },
    },
  },
}
</script>
